import React, { useState, useEffect, useSyncExternalStore } from "react";
import ReactDOM from "react-dom";
import { Box, CircularProgress } from "@mui/material";
import type { Theme } from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import {  StriimTypography } from "@striim/striim-ui";
import StriimCircularProgress from "src/modules/apps/pages/manage-striim/connection-profiles/components/circular-progress/circular-progress";

const styles = {
    banner: {
        display: "flex",
        alignItems: "center",
        gap: 1,
        mt: 1,
        mb: 0.5,
        px: 1.5,
        py: 1,
        borderRadius: 2,
        border: "1px solid",
        borderColor: "greyscale.200",
        backgroundColor: "greyscale.50"
    },
    doneIcon: ({ palette }: Theme) => ({
        fontSize: 18,
        color: palette.success?.main ?? palette.primary.main
    }),
    spinner: ({ palette }: Theme) => ({
        flexShrink: 0,
        color: palette.secondary?.main ?? palette.primary.main
    }),
    label: {
        color: "greyscale.700"
    }
};

export const toolCallStore = {
    _seenNames: new Set<string>(),
    _clusterDone: false,
    _listeners: new Set<() => void>(),
    _version: 0,
    _notifyPending: false,

    _scheduleNotify() {
        if (this._notifyPending) return;
        this._notifyPending = true;
        queueMicrotask(() => {
            this._notifyPending = false;
            this._version++;
            this._listeners.forEach(fn => fn());
        });
    },

    resetCluster() {
        this._seenNames.clear();
        this._clusterDone = false;
        this._version++;
        this._listeners.forEach(fn => fn());
    },

    endCluster() {
        this._clusterDone = true;
        this._scheduleNotify();
    },

    register(name: string) {
        this._seenNames.add(name);
        this._scheduleNotify();
    },

    subscribe(fn: () => void) {
        toolCallStore._listeners.add(fn);
        return () => {
            toolCallStore._listeners.delete(fn);
        };
    },
    getSnapshot() {
        return toolCallStore._version;
    }
};

// Portals the chip into the CopilotKit messages container so it appears
// after the user's message bubble rather than above the input bar.
export const ToolProgressBanner: React.FC = () => {
    useSyncExternalStore(toolCallStore.subscribe, toolCallStore.getSnapshot);

    const [visible, setVisible] = useState(false);
    const [container, setContainer] = useState<Element | null>(null);

    useEffect(() => {
        const el = document.querySelector(".copilotKitMessagesContainer");
        setContainer(el);
    }, []);

    const count = toolCallStore._seenNames.size;
    const isDone = toolCallStore._clusterDone;

    useEffect(() => {
        if (count > 0) {
            setVisible(true);
        } else {
            // count === 0 means resetCluster fired (new user message)
            setVisible(false);
        }
    }, [count, isDone]);

    if (!visible || !container) return null;

    return ReactDOM.createPortal(
        <Box component="aside" sx={styles.banner}>
            {isDone ? (
                <CheckCircleRoundedIcon sx={styles.doneIcon} />
            ) : (
        
                    <StriimCircularProgress size={24} />
                
            )}
            <StriimTypography variant="body4" sx={styles.label}>
                {isDone ? `${count} tool${count !== 1 ? "s" : ""} executed` : `Executing tools… (${count})`}
            </StriimTypography>
        </Box>,
        container
    );
};
